<script setup lang="ts">
import { useToast } from '../toast'
import UiIcon from './UiIcon.vue'

defineProps<{ closeLabel: string }>()

const toast = useToast()
</script>

<template>
  <Teleport to="body">
    <div class="ui-toast-region" role="status" aria-live="polite">
      <div v-if="toast.state.current" :key="toast.state.current.id" class="ui-toast">
        <span class="ui-toast__icon" :class="`ui-toast__icon--${toast.state.current.kind}`">
          <UiIcon :name="toast.state.current.kind === 'error' ? 'alert' : 'check'" :size="14" :stroke-width="2.25" />
        </span>
        <div class="ui-toast__text">
          <span class="ui-toast__title">{{ toast.state.current.title }}</span>
          <span v-if="toast.state.current.message" class="ui-toast__message">{{ toast.state.current.message }}</span>
        </div>
        <button type="button" class="ui-toast__close" :aria-label="closeLabel" @click="toast.dismiss()">
          <UiIcon name="x" :size="16" />
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ui-toast-region {
  position: fixed;
  right: 16px;
  bottom: 16px;
  left: 16px;
  z-index: 200;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
}
.ui-toast {
  pointer-events: auto;
  max-width: 380px;
  background: var(--surface-card);
  border: var(--hairline) solid var(--separator);
  border-radius: var(--radius-popover);
  box-shadow: var(--shadow-md);
  padding: 12px 12px 12px 14px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  animation: aub-fade-in 150ms var(--ease-standard);
}
.ui-toast__icon {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: var(--radius-full);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ui-toast__icon--success {
  background: var(--tint-success-bg);
  color: var(--tint-success-fg);
}
.ui-toast__icon--info {
  background: var(--tint-info-bg);
  color: var(--tint-info-fg);
}
.ui-toast__icon--error {
  background: var(--tint-danger-bg);
  color: var(--tint-danger-fg);
}
.ui-toast__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 2px;
}
.ui-toast__title {
  font-size: var(--type-body-size);
  font-weight: var(--weight-semibold);
}
.ui-toast__message {
  font-size: var(--type-footnote-size);
  line-height: var(--type-footnote-line);
  color: var(--text-secondary);
}
.ui-toast__close {
  width: 28px;
  height: 28px;
  min-width: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: var(--radius-md);
  color: var(--tertiary-label);
  cursor: pointer;
}
.ui-toast__close:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}
</style>
